'use client';

import BaseTrackingService from '@diritto/common/tracker/BaseTrackingService';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import FirebaseTracker from 'trackers/FirebaseTracker';
import GtmTracker from 'trackers/GtmTracker';
import MixpanelTracker from 'trackers/MixpanelTracker';
import AppTrackingService from 'trackers/services/AppTrackingService';
import WebTrackingService from 'trackers/services/WebTrackingService';
import { useClient } from 'ui-hooks/useClient';

interface TrackingState {
  tracker: BaseTrackingService;
}

const TrackingContext = createContext<TrackingState | undefined>(undefined);

export const TrackingProvider = ({ children }: { children: ReactNode }) => {
  const { isApp } = useClient();
  const tracker = useMemo(
    () =>
      isApp
        ? new AppTrackingService()
        : new WebTrackingService(new GtmTracker(), new MixpanelTracker(), new FirebaseTracker()),
    [isApp],
  );
  const value = useMemo(() => ({ tracker }), [tracker]);

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};

export const useTrackingState = () => {
  const context = useContext(TrackingContext);

  if (!context) {
    throw new UsedOutsideProviderError('useTrackingState was used outside of its Provider');
  }

  return useMemo(() => context, [context]);
};
